import type { TravelItinerary } from "@b2bportal/air-booking-api";

const DEFAULT_PROVIDERS_WITHOUT_POST_BOOKING = [
  "consolid",
  "facade",
  "travelfusion",
];

/**
 * Parse the LD flag variant string into a list of providers.
 * The flag is a String type whose value is a JSON string like:
 *   '{"providers": ["consolid", "facade", "travelfusion"]}'
 */
export const parseProvidersFromFlagVariant = (
  flagVariant: string | undefined
): string[] => {
  if (!flagVariant) return DEFAULT_PROVIDERS_WITHOUT_POST_BOOKING;
  try {
    const parsed = JSON.parse(flagVariant);
    return Array.isArray(parsed?.providers) &&
      parsed.providers.every((p: unknown) => typeof p === "string")
      ? parsed.providers
      : DEFAULT_PROVIDERS_WITHOUT_POST_BOOKING;
  } catch {
    return DEFAULT_PROVIDERS_WITHOUT_POST_BOOKING;
  }
};

/**
 * Check whether any provider in the travel itinerary matches the
 * no-post-booking providers list. Handles both SingleTravelItinerary
 * (direct provider) and MultiTravelItinerary (children providers).
 */
export const hasMatchingProvider = (
  travelItinerary: TravelItinerary | undefined,
  providersWithoutPostBooking: string[]
): boolean => {
  if (!travelItinerary) return false;

  const topLevelProvider = travelItinerary.locators?.agent?.provider;

  if (
    topLevelProvider &&
    providersWithoutPostBooking.includes(topLevelProvider)
  ) {
    return true;
  }

  // For MultiTravelItinerary (e.g. hacker fares), the top-level provider is
  // "multiProvider" — check the children's providers instead.
  if (travelItinerary.TravelItinerary === "MultiTravelItinerary") {
    const children = travelItinerary.locators?.children ?? [];
    return children.some((c) =>
      providersWithoutPostBooking.includes(c.agent?.provider ?? "")
    );
  }

  return false;
};
